import { Box, CircularProgress, Flex } from '@chakra-ui/react'

import { useCallback, useMemo, useRef } from 'react'
import { useFormState } from 'react-final-form'
import { ThemeProvider } from '../../../../contexts/ThemeProvider'
import { useResizeObserver } from '../../../../hooks/useResizeObserver'
import { SvgIcon } from '../../../../icons/SvgIcon'
import saveIcon from '../../../../icons/save_old_green.svg'
import { ActionButton } from '../../../Buttons/ActionButton'
import { LoadingOverlay } from '../../../LoadingOverlay'
import { BoxHeader } from '../../../Text/BoxHeader'
import { SimpleFormFooter } from '../../FinalForm'
import { useForm } from '../../FinalForm/FormProvider'
import { UploadProgressOverlay } from '../../FinalForm/UploadProgressView'
import { useFormWizard } from '../../FormWizard/context'
import { RenderStageProps } from '../types'
import { CoverageOptionSelect } from './CoverageOption/CoverageOption'
import { PrimaryCoverageSelect, SecondaryCoverageSelect } from './CoverageSelect'
import { InsuranceInfoStageProvider, useInsuranceInfoStage } from './InsuranceInfoStageContext'
import { InsuranceInfoStepProvider } from './InsuranceInfoStepContext'

const SaveChangesButton = () => {
  const { isSaving } = useInsuranceInfoStage()
  const { readOnly } = useFormWizard()
  const { saveWithoutValidation } = useForm()
  const { dirty } = useFormState({ subscription: { dirty: true } })
  const handleClick = useCallback(async () => {
    await saveWithoutValidation()
  }, [saveWithoutValidation])
  if (readOnly) return null
  return (
    <ActionButton
      opacity={dirty && !isSaving ? 1 : 0}
      transition="opacity 300ms"
      onClick={handleClick}
      gap={1.5}
      size="xs"
      variant="outline">
      <SvgIcon src={saveIcon} />
      Save Changes
    </ActionButton>
  )
}
const InsuranceInfoStageBody = () => {
  const { isSaving, uploads, type } = useInsuranceInfoStage()
  const { readOnly, ReadOnlyFooter } = useFormWizard()

  const bodyRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const { height: bodyHeight } = useResizeObserver(bodyRef, 'border')
  const { height: footerHeight } = useResizeObserver(footerRef, 'border')
  const { height: headerHeight } = useResizeObserver(headerRef, 'border')

  const maxFormHeight = useMemo(
    () => bodyHeight - footerHeight - headerHeight,
    [bodyHeight, footerHeight, headerHeight],
  )

  return (
    <ThemeProvider theme="detailed">
      <Flex ref={bodyRef} position="relative" flexFlow="column" w="100%" height="100%">
        <Flex
          ref={headerRef}
          pr={type === 'pregnancy' ? 10 : 0}
          w="100%"
          gap={2}
          px={3}
          py={2}
          borderBottom="1px solid"
          bg="gray.100"
          borderColor="gray.200">
          <BoxHeader fontSize="xl" color="gray.600">
            Insurance Information
          </BoxHeader>
          <Box>
            <CircularProgress
              position="relative"
              opacity={isSaving ? 1 : 0}
              transition="opacity 300ms"
              isIndeterminate={isSaving}
              size="20px"
              color="gray.500"
            />
          </Box>
          <Flex ml="auto" align="center">
            <SaveChangesButton />
          </Flex>
        </Flex>
        <Box w="100%" position="relative">
          <Box
            bg="gray.50"
            maxH={`${maxFormHeight}px`}
            overflowY="auto"
            w="100%"
            px={3}
            py={2}
            position="relative">
            <CoverageOptionSelect />
            <PrimaryCoverageSelect />
            <SecondaryCoverageSelect />
            <UploadProgressOverlay uploads={uploads} />
          </Box>
          <LoadingOverlay loading={isSaving} />
        </Box>
        <Box ref={footerRef} bg="gray.100" borderTop="1px solid" borderColor="gray.200" px={2}>
          {readOnly ? (
            ReadOnlyFooter ? (
              <ReadOnlyFooter />
            ) : null
          ) : (
            <SimpleFormFooter canSubmitClean submitText="NEXT" />
          )}
        </Box>
      </Flex>
    </ThemeProvider>
  )
}

export const getInsuranceInfoStage = (type: 'pregnancy' | 'gyn') => (props: RenderStageProps) => (
  <InsuranceInfoStageProvider type={type} {...props}>
    <InsuranceInfoStepProvider>
      <InsuranceInfoStageBody />
    </InsuranceInfoStepProvider>
  </InsuranceInfoStageProvider>
)

export const InsuranceInfoStage = getInsuranceInfoStage('pregnancy')
